import { Component } from 'preact';
import RuneSpinner from './RuneSpinner';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD,
  BG_ATMOSPHERE, VIGNETTE, DIALOG_STYLE, FourCorners,
} from '../lib/medievalTheme';
import { preloadMatchAssets } from '../utils/game/matchAssetPreload';
import { t } from '../localization/t';

/**
 * Hard gate between matchmaking and the live game board. Renders a
 * loading panel until every asset the GameBoard's first frame depends
 * on is on disk and warmed into the HTTP cache, then mounts the
 * children. Failure surfaces a Retry. There is no "skip" path.
 *
 * Runs in two phases:
 *   1. Local: the player's own deck, cardbacks, table backdrop, and
 *      (if a fresh track will start) match music. Runs before the
 *      children mount.
 *   2. Opponent: the opponent's deck + the playmat texture. Runs after
 *      children mount, triggered when the parent surfaces an
 *      `opponentDeck` prop (GameBoard pushes it up via
 *      `onOpponentDeckResolved` once it has learned the opposing list
 *      from `roomInfo` or the deck:spawn sync event). The overlay
 *      stays on top of the children for the duration of phase 2 so
 *      the user never sees the table render before every card is
 *      ready to draw.
 *
 * Sessions without a remote deck (solo / spectator) pass
 * `needsOpponentPreload=false` and skip straight from phase 1 to ready.
 */
// How long the overlay must be up before the escape hatch appears. Long
// enough that a normal load never shows it, short enough that a player
// stuck on a match that can never finish loading (crash during match
// creation - the opponentDeck prop never arrives) isn't trapped.
export const ESCAPE_HATCH_DELAY_MS = 10_000;

export default class MatchAssetGate extends Component {
  constructor(props) {
    super(props);
    this.state = {
      phase1Done: false,
      phase2Done: false,
      phase: 'verifying',
      downloaded: 0,
      total: 0,
      error: null,
      escapeReady: false,
    };
  }

  componentDidMount() {
    this._unmounted = false;
    this._runPhase1();
    // Escape hatch: the overlay has no other control while loading, and a
    // part-created match (crash during creation) never resolves, so after a
    // grace period offer a way back to the menu. See onAbandon in app.jsx.
    this._escapeTimer = setTimeout(() => {
      if (!this._unmounted) this.setState({ escapeReady: true });
    }, ESCAPE_HATCH_DELAY_MS);
  }

  componentWillUnmount() {
    this._unmounted = true;
    clearTimeout(this._escapeTimer);
  }

  componentDidUpdate(prevProps) {
    if (
      this.state.phase1Done &&
      !this.state.phase2Done &&
      !this.state.error &&
      !prevProps.opponentDeck &&
      this.props.opponentDeck
    ) {
      this._runPhase2();
    }
  }

  _setProgress = (p) => {
    if (this._unmounted) return;
    this.setState({
      phase: p.phase,
      downloaded: p.downloaded || 0,
      total: p.total || 0,
    });
  };

  _runPhase1 = async () => {
    try {
      await preloadMatchAssets({
        deck: this.props.deck,
        sorceryCards: this.props.sorceryCards,
        matchMusicUrl: this.props.matchMusicUrl,
        // Playmat is deferred to phase 2. The user wants the table
        // texture to land alongside the opponent's cards so the
        // reveal is whole-scene at once.
        playmatUrl: null,
        onProgress: this._setProgress,
      });
      if (this._unmounted) return;
      const skipPhase2 = !this.props.needsOpponentPreload;
      this.setState(
        { phase1Done: true, ...(skipPhase2 ? { phase2Done: true } : {}) },
        () => { if (skipPhase2) this.props.onReady?.(); },
      );
    } catch (err) {
      console.error('[match-gate] phase 1 failed:', err);
      if (!this._unmounted) this.setState({ error: err?.message || String(err) });
    }
  };

  _runPhase2 = async () => {
    try {
      await preloadMatchAssets({
        deck: this.props.opponentDeck,
        sorceryCards: this.props.sorceryCards,
        playmatUrl: this.props.playmatUrl,
        // Music was handled in phase 1; nothing to do here.
        matchMusicUrl: null,
        onProgress: this._setProgress,
      });
      if (this._unmounted) return;
      this.setState({ phase2Done: true }, () => this.props.onReady?.());
    } catch (err) {
      console.error('[match-gate] phase 2 failed:', err);
      if (!this._unmounted) this.setState({ error: err?.message || String(err) });
    }
  };

  _retry = () => {
    const restartPhase1 = !this.state.phase1Done;
    this.setState({
      error: null,
      phase: 'verifying',
      downloaded: 0,
      total: 0,
      ...(restartPhase1 ? { phase1Done: false } : {}),
    }, () => {
      if (restartPhase1) this._runPhase1();
      else this._runPhase2();
    });
  };

  render() {
    const { phase1Done, phase2Done, error, phase, downloaded, total } = this.state;
    const showOverlay = !phase2Done || !!error;
    const showChildren = phase1Done && !error;

    return (
      <>
        {showChildren && this.props.children}
        {showOverlay && this._renderOverlay({ error, phase, downloaded, total, phase1Done })}
      </>
    );
  }

  _renderOverlay({ error, phase, downloaded, total, phase1Done }) {
    const showBar = phase === 'fetching' && total > 0;
    const pct = showBar ? Math.min(100, Math.round((downloaded / total) * 100)) : 0;
    const title = error
      ? t('Asset Load Failed')
      : phase1Done
        ? t('Loading Opponent')
        : t('Preparing the Match');
    const detail = phase === 'verifying'
      ? t('Checking deck assets...')
      : phase === 'fetching'
        ? t('Downloading missing assets...')
        : t('Loading textures...');

    return (
      <div className="fixed inset-0 z-[2200] grid place-items-center" style={{ background: BG_ATMOSPHERE }}>
        <div className="fixed inset-0 pointer-events-none" style={{ background: VIGNETTE }} />

        <div className="relative w-full max-w-[32cqw] p-8" style={{ ...DIALOG_STYLE }}>
          <FourCorners />

          <div className="text-center">
            <img src="/valkenhall-logo.png" alt="Valkenhall" className="h-16 mx-auto mb-6" draggable={false} />

            <h2
              className="text-xl font-bold arena-heading mb-2"
              style={{ color: error ? '#c45050' : TEXT_PRIMARY }}
            >
              {title}
            </h2>

            {!error && (
              <>
                <p className="text-sm mb-4" style={{ color: TEXT_MUTED }}>{detail}</p>
                {showBar ? (
                  <>
                    <div
                      className="w-full h-3 rounded-full overflow-hidden mb-3"
                      style={{ background: `${GOLD} 0.08)`, border: `1px solid ${GOLD} 0.12)` }}
                    >
                      <div
                        className="h-full rounded-full transition-all duration-300"
                        style={{
                          width: `${pct}%`,
                          background: 'linear-gradient(90deg, #8b6914, #d4a843, #c49a38)',
                          boxShadow: '0 0 8px rgba(212,168,67,0.3)',
                        }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs" style={{ color: TEXT_MUTED }}>
                      <span>{t('{done} / {total} cards', { done: downloaded, total })}</span>
                      <span className="font-bold" style={{ color: ACCENT_GOLD }}>{pct}%</span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-center"><RuneSpinner size={56} /></div>
                )}
              </>
            )}

            {error && (
              <>
                <p className="text-sm mb-4" style={{ color: TEXT_MUTED }}>{error}</p>
                <button
                  type="button"
                  className="px-5 py-2 text-sm font-semibold arena-heading transition-all"
                  style={{
                    background: 'linear-gradient(180deg, rgba(212,168,67,0.9) 0%, rgba(160,120,40,0.9) 100%)',
                    border: '1px solid rgba(228,200,100,0.6)',
                    borderRadius: 'var(--radius-md)',
                    color: '#1a1408',
                  }}
                  onClick={this._retry}
                >
                  {t('Retry')}
                </button>
              </>
            )}

            {this.state.escapeReady && this.props.onAbandon && (
              <button
                type="button"
                className="mt-6 px-5 py-2 text-sm font-semibold arena-heading transition-all block mx-auto"
                style={{
                  background: 'transparent',
                  border: `1px solid ${GOLD} 0.35)`,
                  borderRadius: 'var(--radius-md)',
                  color: TEXT_BODY,
                }}
                onClick={this.props.onAbandon}
              >
                {t('Return to Menu')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}
